import * as React from 'react';
import { useSelector } from 'react-redux';
import { ArticleHeading3, ArticleParagraph1, Image, Responsive, UIHeading4, UIText3 } from '@cian/ui-kit';
import { IconActionArrowRight16 } from '@cian/ui-kit-design-tokens/icons';
import { clsx } from 'clsx';

import * as styles from './PromoItem.css';
import { trackCreditFullPriceClick } from '../../containers/PromoDetailsModalContainer/tracking';
import { usePromoDetailsConfig } from '../../hooks/promoDetails';
import { selectPromoDetailsById } from '../../selectors/promos';
import { IPromoItem } from '../../types/promos/promos';
import { FullCreditPriceTooltip } from '../FullCreditPrice/FullCreditTooltip';
import { PromoItemLabel } from '../PromoItemLabel';

interface IPromoItemProps {
  promo: IPromoItem;
  promoId: string;
  onClick?(): void;
}

export const PromoItem: React.FC<IPromoItemProps> = ({ promo, onClick, promoId }) => {
  const factoidsInTwoLines = promo.promoProps.length > 3;

  const details = useSelector(selectPromoDetailsById(promoId));

  const promoModalConfig = usePromoDetailsConfig();
  const fullCreditPriceTooltip = promoModalConfig?.fullCreditPriceModalText;

  const handleKeyUp = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (onClick && ['Enter', 'Space'].includes(event.code)) {
        onClick();
      }
    },
    [onClick],
  );

  const handleFullCreditPriceClick = React.useCallback(() => {
    trackCreditFullPriceClick(Boolean(details.isBankMortgage));
  }, [details]);

  return (
    <article
      className={styles['card']}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyUp={handleKeyUp}
      data-testid="PromoItem"
    >
      <div className={styles['top-content']}>
        <div>
          <div className={styles['labels']}>
            {promo.labels.map(({ text, type }) => (
              <PromoItemLabel key={type} newbuildingIds={promo.newbuildingIds} text={text} type={type} />
            ))}
          </div>
          <ArticleHeading3 data-testid="PromoItemHeading">{promo.promoName}</ArticleHeading3>
          <div className={styles['provider']}>
            <ArticleParagraph1 color="text-secondary-default">{promo.providerName}</ArticleParagraph1>
          </div>
        </div>
        <div className={styles['logo']}>
          <Image
            src={promo.providerLogo}
            height="100%"
            width="100%"
            objectFit="contain"
            data-testid="PromoItemProviderLogo"
          />
        </div>
      </div>
      {promo.promoProps.length > 0 && (
        <ul
          className={clsx(styles['bottom-content'], { [styles['two-lines']]: factoidsInTwoLines })}
          data-testid="PromoItemFactoids"
        >
          {promo.promoProps.map((prop, index) => (
            <li className={styles['factoid']} key={index}>
              <div className={styles['factoid-row-title']}>
                <UIText3 color="text-secondary-default">{prop.title}</UIText3>
                <Responsive desktop tablet>
                  {prop.type === 'fullCreditPrice' && fullCreditPriceTooltip && (
                    <FullCreditPriceTooltip
                      tooltip={fullCreditPriceTooltip}
                      placement="top"
                      trackClickHandler={handleFullCreditPriceClick}
                    />
                  )}
                </Responsive>
              </div>

              {!prop.valueChange && (
                <div className={styles['factoid-row-value']}>
                  <UIHeading4 color={prop.type === 'fullCreditPrice' ? 'text-secondary-default' : undefined}>
                    {prop.value}
                  </UIHeading4>
                  <Responsive phone>
                    {prop.type === 'fullCreditPrice' && fullCreditPriceTooltip && (
                      <FullCreditPriceTooltip
                        tooltip={fullCreditPriceTooltip}
                        placement="top"
                        trackClickHandler={handleFullCreditPriceClick}
                      />
                    )}
                  </Responsive>
                </div>
              )}

              {prop.valueChange && (
                <div className={styles['changed-value']}>
                  <UIHeading4>{prop.value}</UIHeading4>
                  <div className={styles['changed-value-icon']}>
                    <IconActionArrowRight16 color="icon-main-default" />
                  </div>
                  <UIHeading4>{prop.valueChange.newValue}</UIHeading4>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

PromoItem.displayName = 'PromoItem';
